import * as React from "react"
import {
  useChartBase,
  injectStyle,
  resolveColor,
  CHART_TONE,
  type NumberFormatter,
} from "./chartStyles"

/*
 * MARK V — ProgressMeter. Barra horizontal KPI-vs-meta p/ dashboards densos
 * (margem realizada vs meta, execução orçamentária). Label + valor no topo,
 * track com porção preenchida, marcador de meta opcional e auto-tom (verde no
 * alvo, âmbar perto, vermelho abaixo). Porte fiel de
 * mark-v-design-system/components/charts/ProgressMeter.jsx.
 */

const CSS = `
.mv-meter{ display:flex; flex-direction:column; gap:6px; }
.mv-meter__top{ display:flex; align-items:baseline; justify-content:space-between; gap:12px; }
.mv-meter__label{ font-size:var(--markv-text-sm); color:var(--markv-text-600); }
.mv-meter__value{ font-family:var(--markv-font-display); font-weight:var(--markv-weight-bold);
  font-size:var(--markv-text-md); color:var(--markv-text-700); font-variant-numeric:tabular-nums; }
.mv-meter__value small{ font-family:var(--markv-font-body); font-weight:var(--markv-weight-regular);
  font-size:var(--markv-text-xs); color:var(--markv-text-400); margin-left:4px; }
.mv-meter__track{ position:relative; height:var(--mv-meter-h,8px); border-radius:var(--markv-radius-full);
  background:var(--markv-chart-track); overflow:visible; }
.mv-meter__fill{ position:absolute; inset:0 auto 0 0; border-radius:var(--markv-radius-full);
  transition:width var(--markv-duration-slow) var(--markv-ease-out); }
.mv-meter__target{ position:absolute; top:-3px; bottom:-3px; width:2px; border-radius:2px;
  background:var(--markv-text-500); }
.mv-meter__target::after{ content:""; position:absolute; left:50%; top:-4px; width:5px; height:5px;
  transform:translateX(-50%) rotate(45deg); background:var(--markv-text-500); }
.mv-meter__foot{ display:flex; align-items:center; justify-content:space-between;
  font-size:var(--markv-text-2xs); color:var(--markv-text-400); }
`

export type ProgressMeterProps = {
  value: number
  max?: number
  target?: number
  label?: React.ReactNode
  valueLabel?: React.ReactNode
  caption?: React.ReactNode
  tone?: string
  thickness?: number
  format?: NumberFormatter
  className?: string
}

export function ProgressMeter({
  value,
  max = 100,
  target,
  label,
  valueLabel,
  caption,
  tone = "auto",
  thickness = 8,
  format = (v) => v,
  className = "",
}: ProgressMeterProps) {
  useChartBase()
  React.useEffect(() => injectStyle("mv-meter-styles", CSS), [])

  const ratio = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0
  let color: string
  if (tone === "auto") {
    const t = target ?? max
    color =
      value >= t
        ? CHART_TONE.positive
        : value >= t * 0.85
          ? "var(--markv-warning-500)"
          : CHART_TONE.negative
  } else {
    color = resolveColor(tone, CHART_TONE.primary)
  }

  return (
    <div className={`mv-chart mv-meter ${className}`.trim()}>
      {(label != null || valueLabel != null) && (
        <div className="mv-meter__top">
          {label != null && <span className="mv-meter__label">{label}</span>}
          {valueLabel != null && <span className="mv-meter__value">{valueLabel}</span>}
        </div>
      )}
      <div
        className="mv-meter__track"
        style={{ "--mv-meter-h": `${thickness}px` } as React.CSSProperties}
      >
        <div className="mv-meter__fill" style={{ width: `${ratio * 100}%`, background: color }} />
        {target != null && max > 0 && (
          <div
            className="mv-meter__target"
            style={{ left: `${Math.min(100, (target / max) * 100)}%` }}
            title={`Meta: ${format(target)}`}
          />
        )}
      </div>
      {(caption != null || target != null) && (
        <div className="mv-meter__foot">
          <span>{caption}</span>
          {target != null && <span>Meta {format(target)}</span>}
        </div>
      )}
    </div>
  )
}
